import { getAppConfig } from "@/betby/infrastructure/config/appConfig";
import {
  createJsonStorageAdapter,
  localStorageAdapter,
} from "@/betby/adapters/storage/storageAdapter";
import { getSessionTicketQueryParam } from "@/betby/adapters/router/queryParamsAdapter";
import { useNavStore } from "@/betby/store/navStore";
import {
  getCachedSessionTicket,
  cacheSessionTicket,
} from "@/betby/models/auth/sessionTicketCache";

export function getSessionTicketFromStorage(): string | null {
  const jsonStorage = createJsonStorageAdapter(localStorageAdapter);
  const stored = jsonStorage.getJSON<{
    state?: { session_ticket?: string | null };
  }>(getAppConfig().storage.keys.nav);
  return stored?.state?.session_ticket ?? null;
}

export function getSessionTicketFromStore(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  return useNavStore.getState().session_ticket ?? null;
}

/**
 * Resolve session ticket from multiple sources
 * Priority: Store > URL Query > Storage > Cache
 *
 * When a valid session ticket is found, it's cached in memory with 10min TTL
 */
export function resolveSessionTicket(search?: string): string | null {
  if (typeof window === "undefined" && !search) {
    return null;
  }

  const effectiveSearch = search ?? window.location.search;

  // 1. Check store (most recent, set by props)
  const ticketFromStore = getSessionTicketFromStore();
  if (ticketFromStore) {
    cacheSessionTicket(ticketFromStore);
    return ticketFromStore;
  }

  // 2. Check URL query param
  const ticketFromUrl = getSessionTicketQueryParam(effectiveSearch);
  if (ticketFromUrl) {
    cacheSessionTicket(ticketFromUrl);
    return ticketFromUrl;
  }

  // 3. Check storage (persisted)
  const ticketFromStorage = getSessionTicketFromStorage();
  if (ticketFromStorage) {
    cacheSessionTicket(ticketFromStorage);
    return ticketFromStorage;
  }

  // 4. Check in-memory cache (with TTL validation)
  const ticketFromCache = getCachedSessionTicket();
  if (ticketFromCache) {
    return ticketFromCache;
  }

  return null;
}
